<template>
  <div class="notification-container">
    <!-- Badge principal -->
    <button
        class="notification-badge"
        :class="{ 'bg-green-600 p-1 rounded-full text-white hover:text-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-800 focus:ring-white transition-colors':true, 'has-notifications': hasNotifications }"
        @click="togglePanel"
    >
      <Icon name="heroicons:bell" class="h-5 w-5 notification-bell " />
      <span v-if="totalNotifications > 0" class="badge-count">
        {{ totalNotifications > 99 ? '99+' : totalNotifications }}
      </span>
    </button>

    <!-- Panel de notifications -->
    <Teleport to="body">
      <div
          v-if="showPanel"
          class="notification-overlay"
          @click="showPanel = false"
      >
        <div
            ref="panelRef"
            class="notification-panel"
            @click.stop
        >
          <div class="panel-header">
            <h3 class="text-lg font-semibold">{{ $t('notifications.title') }}</h3>
            <div class="flex gap-2">
              <button
                  @click="refresh"
                  :disabled="isLoading"
                  class="btn-icon"
                  :title="$t('notifications.actions.refresh')"
              >
                <Icon
                    name="heroicons:arrow-path"
                    class="h-4 w-4"
                    :class="{ 'animate-spin': isLoading }"
                />
              </button>
              <button
                  @click="markAllAsSynced"
                  :disabled="!hasNotifications || isLoading"
                  class="btn-secondary"
              >
                {{ $t('notifications.actions.mark_all_read') }}
              </button>
              <button
                  @click="showPanel = false"
                  class="btn-icon"
                  :title="$t('notifications.actions.close')"
              >
                <Icon name="heroicons:x-mark" class="h-4 w-4" />
              </button>
            </div>
          </div>

          <div class="panel-content">
            <div v-if="!hasNotifications" class="no-notifications">
              <Icon name="heroicons:check-circle" class="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p>{{ $t('notifications.states.no_notifications') }}</p>
            </div>

            <div v-else class="notifications-list">
              <div
                  v-for="notification in notificationsByEntity"
                  :key="notification.entity"
                  class="notification-item"
                  @click="handleEntityClick(notification.entity)"
              >
                <div class="notification-icon">
                  <Icon :name="getEntityIcon(notification.entity)" class="h-5 w-5" />
                </div>
                <div class="notification-info">
                  <h4 class="font-medium">{{ getEntityDisplayName(notification.entity) }}</h4>
                  <div class="flex gap-2 text-sm text-gray-600">
                    <span v-if="notification.new.count > 0" class="text-green-600">
                      {{ notification.new.count }} {{ $t('notifications.status.new') }}
                    </span>
                    <span v-if="notification.updated.count > 0" class="text-orange-600">
                      {{ notification.updated.count }} {{ $t('notifications.status.updated') }}
                    </span>
                  </div>
                </div>
                <div class="notification-badge">
                  {{ notification.total }}
                </div>
              </div>
            </div>
          </div>

          <div class="panel-footer">
            <p class="text-xs text-gray-500">
              {{ $t('notifications.states.last_check') }} {{ formatLastCheck }}
            </p>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
const { t } = useI18n()
const showPanel = ref(false)
const router = useRouter()
import { onClickOutside } from '@vueuse/core'

const {
  hasNotifications,
  totalNotifications,
  notificationsByEntity,
  isLoading,
  lastCheck,
  checkForUpdates,
  markAllAsSynced,
  markEntityAsSynced
} = useNotifications()

const togglePanel = () => {
  showPanel.value = !showPanel.value
}

const refresh = async () => {
  await checkForUpdates()
}

const handleEntityClick = async (entityName) => {
  const routes = {
    users: '/admin/users',
    roles: '/admin/roles',
    contactRequests: '/admin/contact-requests'
  }

  if (routes[entityName]) {
    await markEntityAsSynced(entityName)
    await navigateTo(routes[entityName])
    showPanel.value = false
  }
}

const getEntityIcon = (entity) => {
  const icons = {
    users: 'heroicons:users',
    roles: 'heroicons:shield-check',
    contactRequests: 'heroicons:envelope'
  }
  return icons[entity] || 'heroicons:document'
}

const getEntityDisplayName = (entity) => {
  return t(`notifications.entities.${entity}`)
}

const formatLastCheck = computed(() => {
  if (!lastCheck.value) return t('notifications.states.never')

  return new Intl.DateTimeFormat(useI18n().locale.value === 'fr' ? 'fr-FR' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(lastCheck.value))
})

const panelRef = ref(null)
// Fermer au clic extérieur
onClickOutside(panelRef, () => {
  showPanel.value = false
})
</script>

<style scoped>
.notification-container {
  @apply relative ;
}

.notification-badge {
  @apply relative ;
}

.notification-badge.has-notifications {
  @apply text-white;
}

.badge-count {
  @apply absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center;
}

.notification-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end pt-16 pr-4;
}

.notification-panel {
  @apply bg-white rounded-lg shadow-xl w-96 h-fit max-h-[80vh] overflow-hidden;
}

.panel-header {
  @apply flex justify-between items-center p-4 border-b border-gray-200;
}

.panel-content {
  @apply max-h-96 overflow-y-auto;
}

.no-notifications {
  @apply p-8 text-center text-gray-500;
}

.notifications-list {
  @apply divide-y divide-gray-100;
}

.notification-item {
  @apply flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors;
}

.notification-icon {
  @apply w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center;
}

.notification-info {
  @apply flex-1;
}

.notification-badge {
  @apply bg-green-600 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[20px] text-center;
}

.panel-footer {
  @apply p-3 bg-gray-50 border-t border-gray-200;
}

.btn-icon {
  @apply p-1 rounded hover:bg-gray-100 transition-colors;
}

.btn-secondary {
  @apply px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors;
}

.notification-bell {
  animation: ring 2s ease infinite;
  transform-origin: 50% 0;
}

@keyframes ring {
  0% { transform: rotate(0); }
  1% { transform: rotate(30deg); }
  3% { transform: rotate(-28deg); }
  5% { transform: rotate(34deg); }
  7% { transform: rotate(-32deg); }
  9% { transform: rotate(30deg); }
  11% { transform: rotate(-28deg); }
  13% { transform: rotate(26deg); }
  15% { transform: rotate(-24deg); }
  17% { transform: rotate(22deg); }
  19% { transform: rotate(-20deg); }
  21% { transform: rotate(18deg); }
  23% { transform: rotate(-16deg); }
  25% { transform: rotate(14deg); }
  27% { transform: rotate(-12deg); }
  29% { transform: rotate(10deg); }
  31% { transform: rotate(-8deg); }
  33% { transform: rotate(6deg); }
  35% { transform: rotate(-4deg); }
  37% { transform: rotate(2deg); }
  39% { transform: rotate(-1deg); }
  41% { transform: rotate(1deg); }
  43% { transform: rotate(0); }
  100% { transform: rotate(0); }
}
</style>